import { ChartSettingsT } from 'business/definitions';
import { FontSize } from './types';
import {
    AvailableSettings,
    StaticSettings,
} from 'routes/Explore/components/Settings/components/ChartSettingsDrawer/helpers/definitions';

/**
 * Add a property to an object if the value of the property is not undefined.
 * @param obj - Object to add the property to.
 * @paran key - The key for the property under which it will be added to the object.
 * @param value - The value of the property.
 * @returns A copy of {@param obj} with the property set if the value is not undefined.
 */
export const setIfDefined = <T extends object, K extends keyof T>(
    obj: T,
    key: K,
    value: T[K] | undefined,
): T => {
    if (value === undefined) {
        return obj;
    }
    return {
        ...obj,
        [key]: value,
    };
};

/**
 * Typeguard for {@link PlainResponseDataValue}.
 */
const isPlainResponseDataValueT = (
    value: unknown,
): value is PlainResponseDataValueT => {
    return (
        typeof value === 'string' ||
        typeof value === 'number' ||
        value instanceof Date
    );
};

/**
 * Typeguard for {@link DictAllDataValueT}, the 'tree' values,
 * usually returned when requesting an orgChart.
 */
export const isDictAllDataValueT = (
    value: unknown,
): value is DictAllDataValueT => {
    return (
        typeof value === 'object' &&
        value !== null &&
        'label' in value &&
        'value' in value &&
        typeof value.label === 'string' &&
        isPlainResponseDataValueT(value.value)
    );
};

/**
 * Typeguard for {@link FontSize}.
 */
export const isFontSize = (value: string): value is FontSize => {
    return value === 'sm' || value === 'md' || value === 'lg';
};

/**
 * Convert a PlainResponseDataValueT to a number.
 */
export const plainResponseToFloatValue = (
    value: PlainResponseDataValueT,
): number => {
    if (typeof value === 'string') {
        return parseFloat(value);
    }
    if (typeof value === 'boolean') {
        return Number(value);
    }
    if (value instanceof Date) {
        return value.getTime();
    }
    if (isDictAllDataValueT(value)) {
        return plainResponseToFloatValue(value.value);
    }
    return value;
};

export const getValidSettings = (
    settings: ChartSettingsT,
    validKeys: (AvailableSettings | StaticSettings)[],
): ChartSettingsT => {
    const newSettings = {};
    for (const key of validKeys) {
        if (key in settings) {
            // @ts-expect-error [typing] we don't care that it is of type any.
            newSettings[key] = settings[key];
        }
    }
    return newSettings;
};

/**
 * A utility type that extract a type from an Union type.
 * This is useful for EChart because echart expose a lot of type that are union,
 * like `type A = B | B[];` without exposing the inner type (B here).
 * So if we want to type something with B and not B[], it would be impossible, but with this we can
 * type with ExtractType<A>.
 */
export type ExtractType<T> = Extract<T, T extends infer U ? U : never>;
